import React, { useState, useMemo } from 'react';
import { InputActionMeta } from 'react-select';
import CSS from 'csstype';
import '../../App.css';
import { useTranslation } from 'react-i18next';
import { LockKeyhole, LockKeyholeOpen } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// functions
import {
  apiPrefix,
  extractFileFromAccessUrl,
  extractFileFromObsPublisherDid,
} from '../../utils/functions';

// classes
import ToolButton from '../../tools/tool-button';
import { ToolButtonType, ToolButtonWidth } from '../../tools/tool-button';
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import EditableSelect from '../../tools/editable-select';
import { Event, EventType, EventTarget, DataCollection } from '../../utils/events';

// types
import { RucioLocationsType, UserType } from '../../utils/types';

// types specifically for this component.
import { SelectedRowsType } from './search-catalog-types';

// data-management types.
import { handleEvent } from '../../app-actions';

// Select-box custom styles.
import { customStylesDark } from '../../utils/custom-styles-select-box';
import { customStylesLight } from '../../utils/custom-styles-select-box';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  rowCheckedHandler,
  setExistingCollection,
  setExistingCollectionText,
} from './search-catalog-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SelectedItemsTab(props: { hidden: boolean }) {
  //
  // class-level constants.
  const TABLE_COL: CSS.Properties = {
    padding: '0px 5px 0px 5px',
    wordWrap: 'break-word',
    textAlign: 'left',
    background: 'transparent',
  };
  const TABLE_COL_TOP: CSS.Properties = { paddingTop: '8px' };
  const TABLE_COL_BOTTOM: CSS.Properties = { paddingBottom: '8px' };

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL
  // --------------------

  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_submittingJob: boolean = useAppSelector((state) => state.global.submittingJob);
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_userType: UserType = useAppSelector(
    (state) => state.global.userType ?? UserType.STANDARD,
  );

  // DATA
  // --------------------

  // rucio locations.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  ).filter((filter) => filter.catalog.indexOf('search-catalog') > -1);

  // namespace-access lookup built from rucio locations. undefined means the
  // access check hasn't come back yet (checking state).
  const accessByKey: Map<string, boolean | undefined> = useMemo(() => {
    const map = new Map<string, boolean | undefined>();
    for (const r of sD_rucioLocations) {
      map.set(`${r.namespace}:${r.filename}`, r.userCanAccessNamespace);
    }
    return map;
  }, [sD_rucioLocations]);

  const resolveAccessState = (item: {
    namespace: string;
    filename: string;
  }): AccessState => {
    const value = accessByKey.get(`${item.namespace}:${item.filename}`);
    if (value === true) return 'accessible';
    if (value === false) return 'inaccessible';
    return 'checking';
  };

  // LOCAL
  // --------------------

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  const sExistingCollection: { value: number; label: string } = useAppSelector(
    (state) => state.searchCatalog.existingCollection,
  );
  const sExistingCollectionText: string = useAppSelector(
    (state) => state.searchCatalog.existingCollectionText,
  );
  const sCollections: { value: number; label: string }[] = useAppSelector(
    (state) => state.searchCatalog.collections,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	raise a create-collection popup message.
  //
  //	------------------------------------------------------------

  const [sRaiseCreateCollectionMessage, setRaiseCreateCollectionMessage] =
    useState<any>(undefined);

  const setCreateCollectionMessageObject = (newFunction: any) => {
    setRaiseCreateCollectionMessage(() => newFunction);
  }; // setCreateCollectionMessageObject

  //	------------------------------------------------------------
  //
  //	raise an add-to-collection popup message.
  //
  //	------------------------------------------------------------

  const [sRaiseAddToCollectionMessage, setRaiseAddToCollectionMessage] =
    useState<any>(undefined);

  const setAddToCollectionMessageObject = (newFunction: any) => {
    setRaiseAddToCollectionMessage(() => newFunction);
  }; // setAddToCollectionMessageObject

  //	------------------------------------------------------------
  //
  //	raise an existing-collection-name popup message.
  //
  //	------------------------------------------------------------
  const [sExistingCollectionNameError, setExistingCollectionNameError] =
    useState<boolean>(false);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Download a selected catalogue item using its access URL.
  //
  //	------------------------------------------------------------

  function downloadSelectedItem(item: SelectedRowsType) {
    //
    if (item.accessUrl === undefined || item.accessUrl === '') {
      return;
    }

    let fileDetails: { namespace: string; filename: string } =
      extractFileFromObsPublisherDid({
        obsPublisherDid: item.obsPublisherDid,
      });
    if (fileDetails.namespace === '') {
      fileDetails = extractFileFromAccessUrl({ accessUrl: item.accessUrl });
    }

    const filename: string =
      fileDetails.filename !== '' ? fileDetails.filename : item.filename;

    fetch(item.accessUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        if (link.parentNode !== null) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.log('error downloading selected item: ', error);
      });
    //
  } // downloadSelectedItem

  //	------------------------------------------------------------
  //
  //	Handler for Select box onInputChange event.
  //
  //	------------------------------------------------------------

  const handleDeleteCollection = async (collectionId: string | number) => {
    //
    try {
      //
      console.log('Deleting collection: ' + collectionId);

      const response = await fetch(apiPrefix() + '/v1/delete_data_collections', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([collectionId]),
        credentials: 'include',
      });

      if (response) {
        //
        if (sExistingCollection?.value === collectionId) {
          dispatch(setExistingCollection({ value: -1, label: '' }));
          dispatch(setExistingCollectionText(''));
        }

        // raise an event to handle the refresh of the collections.
        const project: string | undefined = sG_project?.name ?? undefined;
        const newEvent: Event = {
          eventTarget: EventTarget.GLOBAL,
          eventType: EventType.DATA_COLLECTION_DELETED,
          parameters: project,
        };

        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
    } catch (error) {
      console.error('Error deleting collection: ', error);
    }
    //
  }; // handleDeleteCollection

  //	------------------------------------------------------------
  //
  //	Handler for Select box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeExistingCollectionHandler = (option: any) => {
    //
    // raise an onChange event.
    if (option !== null) {
      dispatch(setExistingCollection(option));
      dispatch(setExistingCollectionText(option.label));
    } else {
      dispatch(setExistingCollection({ value: -1, label: '' }));
      dispatch(setExistingCollectionText(''));
    }
    //
  }; // onChangeExistingCollectionHandler

  //	------------------------------------------------------------
  //
  //	Handler for onClick events (buttons).
  //
  //	------------------------------------------------------------

  const onClickHandlerBtn = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const btn: HTMLButtonElement = event.currentTarget;

    // if we've clicked on a button to remove all items.
    if (btn.name === 'deselect-all') {
      //
      // get the list of selected rows from state.
      let rows: SelectedRowsType[] = sSelectedRows.slice();

      // loop over all the rows, adding them to the selected-items list.
      let ticked: boolean[] = [];
      for (let i: number = 0; i < sSelectedRows.length; i++) {
        ticked.push(false);
      }

      // update state.
      dispatch(rowCheckedHandler({ rows: rows, ticked: ticked }));
      //
    }

    if (btn.name === 'download-all') {
      for (let i: number = 0; i < sSelectedRows.length; i++) {
        downloadSelectedItem(sSelectedRows[i]);
      }
    }

    // if we've clicked on a button to unselect a row.
    if (btn.name.length > 12)
      if (btn.name.slice(0, 12) === 'btnUnselect_') {
        //
        // get the rest of the identifier, and convert to numeric.
        let rowID: string = btn.name.slice(12 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // raise an onChange event.
        if (rowIDNumeric > -1) {
          dispatch(
            rowCheckedHandler({
              rows: [Object.create(sSelectedRows[rowIDNumeric])],
              ticked: [false],
            }),
          );
        }
        //
      }

    // if we've clicked on a button to create a collection.
    if (btn.name === 'create-collection') {
      //
      // build list of dids from the selected items.
      let dids: {
        namespace: string;
        filename: string;
      }[] = [];
      for (let i: number = 0; i < sD_rucioLocations.length; i++)
        if (sD_rucioLocations[i].namespace !== '' && sD_rucioLocations[i].filename !== '')
          dids.push({
            namespace: sD_rucioLocations[i].namespace,
            filename: sD_rucioLocations[i].filename,
          });

      // create a new data collection.
      const dataCollection: DataCollection = {
        project: sG_project,
        dataCollection: {
          id: -1,
          name: sExistingCollectionText,
        },
        dids: dids,
        messageSearchCatalog: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_CATALOG,
        eventType: EventType.CREATE_NEW_DATA_COLLECTION,
        parameters: dataCollection,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }

    // if we've clicked on a button to add to a collection.
    if (btn.name === 'add-to-collection') {
      //
      // build list of dids from the selected items.
      let dids: {
        namespace: string;
        filename: string;
      }[] = [];
      for (let i: number = 0; i < sD_rucioLocations.length; i++)
        if (sD_rucioLocations[i].namespace !== '' && sD_rucioLocations[i].filename !== '')
          dids.push({
            namespace: sD_rucioLocations[i].namespace,
            filename: sD_rucioLocations[i].filename,
          });

      // add to an existing data collection.
      const dataCollection: DataCollection = {
        dataCollection: {
          id: sExistingCollection !== undefined ? sExistingCollection.value : -1,
          name: sExistingCollection !== undefined ? sExistingCollection.label : '',
        },
        project: sG_project,
        dids: dids,
        messageSearchCatalog: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.ADD_TO_EXISTING_DATA_COLLECTION,
        parameters: dataCollection,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  }; // onClickHandlerBtn

  //	------------------------------------------------------------
  //
  //	Handler for Select box onInputChange event.
  //
  //	------------------------------------------------------------

  const onInputChangeSelectHandler = (inputValue: string, meta: InputActionMeta) => {
    //
    if (meta.action === 'input-change') {
      //
      // find this item in the list of options.
      const index: number = sCollections.findIndex(
        (item) => item.label.toUpperCase() === inputValue.toUpperCase(),
      );

      // update the text in the Select box.
      dispatch(setExistingCollectionText(inputValue));

      // update the selected collection if the text exactly matches.
      if (index > -1) {
        dispatch(setExistingCollection(sCollections[index]));
      } else {
        dispatch(setExistingCollection({ value: -1, label: '' }));
      }
    }
    //
  }; // onInputChangeSelectHandler

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	A HTML component that contains the action buttons for each
  //	row.
  //
  //	------------------------------------------------------------

  function Actions(args: { index: number }) {
    //
    //	--------------------------------------------------------------------------
    //
    //	C O M P O N E N T   C O D E
    //
    //	--------------------------------------------------------------------------

    return (
      <div
        className="selected-items-row-actions"
        style={{
          display: props.hidden === true ? 'none' : 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          margin: '0px 0px 0px 0px',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <ToolButton
          name={'btnUnselect_' + args.index.toString()}
          text={t('Deselect')}
          tooltip={t('Deselect')}
          onClick={onClickHandlerBtn}
          type={ToolButtonType.SECONDARY}
          width={ToolButtonWidth.NARROW}
        />
      </div>
    );
    //
  } // Actions

  //	------------------------------------------------------------
  //
  //	A HTML component that takes a list of sources and renders
  //	them as a list of table rows.
  //
  //	------------------------------------------------------------

  function itemDisplayName(item: SelectedRowsType) {
    if (item.targetName !== undefined && item.targetName !== '') {
      return item.targetName;
    }
    return t('Unknown source');
  } // itemDisplayName

  function itemPath(item: SelectedRowsType) {
    return '[' + item.namespace + ':' + item.filename + ']';
  } // itemPath

  function TableRows(args: { dataItems: SelectedRowsType[] }) {
    //
    //	--------------------------------------------------------------------------
    //
    //	F U N C T I O N S
    //
    //	--------------------------------------------------------------------------

    //	-------------------------------------------------
    //
    //	C O M P O N E N T   C O D E
    //
    //	-------------------------------------------------

    try {
      return (
        <>
          {args.dataItems.map((item, index) => {
            const accessState: AccessState = resolveAccessState(item);
            const showLockIcon = sG_loggedIn === true;
            return (
              <>
                <tr className="search-results-table-row text-colour">
                  <td
                    className="selected-items-actions-col"
                    style={{ ...TABLE_COL, ...TABLE_COL_TOP, ...TABLE_COL_BOTTOM }}
                    rowSpan={2}
                  >
                    {Actions({ index: index })}
                  </td>
                  <td style={{ ...TABLE_COL, ...TABLE_COL_TOP }}>
                    {itemDisplayName(item)}
                  </td>
                  <td
                    style={{ ...TABLE_COL, ...TABLE_COL_TOP, ...TABLE_COL_BOTTOM }}
                    rowSpan={2}
                  >
                    {item.dataProductType?.toUpperCase() ?? ''}
                  </td>
                  <td
                    style={{ ...TABLE_COL, ...TABLE_COL_TOP, ...TABLE_COL_BOTTOM }}
                    rowSpan={2}
                  >
                    size unavailable
                  </td>
                  <td
                    style={{ ...TABLE_COL, ...TABLE_COL_TOP, ...TABLE_COL_BOTTOM }}
                    rowSpan={2}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {showLockIcon && <AccessLockIcon accessState={accessState} />}
                    </div>
                  </td>
                </tr>
                <tr className="search-results-table-row text-colour" data-muted="T">
                  <td style={{ ...TABLE_COL, ...TABLE_COL_BOTTOM }}>{itemPath(item)}</td>
                </tr>
              </>
            );
          })}
        </>
      );
    } catch (e) {
      return <></>;
    }
  } // TableRows

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: props.hidden === true ? 'none' : 'flex',
        margin: '10px 0px 0px 0px',
        flexDirection: 'column',
      }}
    >
      <Scrollbox
        id="search-catalog-scrollbox"
        direction="vertical"
        /*width="100%"*/
        flex="1 1"
        flexDirection="column"
        align="normal"
      >
        <div
          className="text-colour"
          style={{ overflowX: 'auto', height: '100%', flex: '1 1', position: 'relative' }}
        >
          {sSelectedRows.length === 0 && (
            <div
              className="text-colour"
              data-muted="T"
              style={{
                margin: '10px 10px',
              }}
            >
              No data items selected
            </div>
          )}
          <table
            className="search-results-table selected-items-table"
            style={{ textAlign: 'left', margin: '0px', tableLayout: 'fixed' }}
          >
            <colgroup>
              <col key="Actions_col" className="sc-col-actions" />
              <col key="name" />
              <col key="type" className="sc-col-type" />
              <col key="size" className="sc-col-size" />
              <col key="lock" className="sc-col-lock" />
            </colgroup>
            <thead style={{ display: 'none' }}>
              <tr className="search-results-table-row-header">
                <th style={TABLE_COL} />
                <th style={TABLE_COL} />
                <th style={TABLE_COL} />
                <th style={TABLE_COL} />
                <th style={TABLE_COL} />
              </tr>
            </thead>
            <tbody>
              {TableRows({
                dataItems: sSelectedRows,
              })}
            </tbody>
          </table>
        </div>
      </Scrollbox>
      <div style={{ flex: '0 0 10px' }} />
      <Scrollbox
        direction="horizontal"
        flex="0 0 auto"
        width="auto"
        flexDirection="column"
      >
        <div style={{ flex: '0 0 10px' }} />
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div style={{ flex: '0 0 10px' }} />
          <ToolButton
            name="deselect-all"
            text="Deselect all"
            tooltip="Deselect all selected items"
            disabled={sSelectedRows.length === 0}
            onClick={onClickHandlerBtn}
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ flex: '0 0 10px' }} />
          <ToolButton
            name="download-all"
            text="Download all"
            tooltip="Download all selected items"
            disabled={sSelectedRows.length === 0}
            onClick={onClickHandlerBtn}
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ display: 'none' }}>
            <div style={{ flex: '0 0 10px' }} />
            {sG_userType === UserType.DEVELOPER && (
              <div
                style={{
                  border: '1pt solid #555',
                  borderRadius: '6px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flex: '0 0 650px',
                  padding: '10px',
                  position: 'relative',
                }}
              >
                <div
                  className="text-colour window-background"
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    width: 'auto',
                    padding: '0px 5px 0px 5px',
                  }}
                >
                  {t('Data collections')}
                </div>
                <div style={{ flex: '0 0 5px' }} />
                <label
                  id="search-catalog-tab-collection-label"
                  className="text-colour"
                  style={{
                    marginRight: '10px',
                  }}
                >
                  Collection:
                </label>
                <div
                  id="existingCollectionNameDiv"
                  style={{
                    alignItems: 'center',
                    flex: '0 0 250px',
                    border:
                      sExistingCollectionNameError === true
                        ? '2pt solid red'
                        : '2pt solid transparent',
                  }}
                >
                  <EditableSelect
                    aria-labelledby="search-catalog-tab-collection-label"
                    gatewayTheme={sG_darkMode === true ? 'dark' : 'light'}
                    styles={sG_darkMode === true ? customStylesDark : customStylesLight}
                    isDisabled={sD_rucioLocations.length === 0}
                    options={sCollections}
                    placeholder={t('Collection')}
                    value={sExistingCollection}
                    onChange={onChangeExistingCollectionHandler}
                    inputValue={sExistingCollectionText}
                    onInputChange={onInputChangeSelectHandler}
                    onDelete={handleDeleteCollection}
                    controlShouldRenderValue={false}
                  />
                </div>
                <div
                  id="createCollectionDiv"
                  style={{
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    margin: '0px 0px 0px 10px',
                  }}
                >
                  <ToolButton
                    key={t('Create')}
                    name="create-collection"
                    onClick={onClickHandlerBtn}
                    tooltip="Create a new data collection"
                    text={t('Create')}
                    type={ToolButtonType.SECONDARY}
                    disabled={
                      sD_rucioLocations.length === 0 ||
                      sExistingCollectionText === '' ||
                      sExistingCollection.value > -1
                    }
                  />
                </div>
                <div
                  id="addToCollectionDiv"
                  style={{
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'row',
                    margin: '0px 0px 0px 10px',
                  }}
                >
                  <ToolButton
                    key={t('Add to')}
                    name="add-to-collection"
                    onClick={onClickHandlerBtn}
                    tooltip="Add to an existing data collection"
                    text={t('Add to')}
                    type={ToolButtonType.SECONDARY}
                    disabled={
                      sD_rucioLocations.length === 0 || sExistingCollection.value === -1
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              display: sG_submittingJob === true ? 'flex' : 'none',
              flex: '0 0 150px',
              alignItems: 'center',
              textAlign: 'center',
              margin: '0px 10px 0px 10px',
            }}
          >
            <PleaseWait text="Submitting job..." visible={true} />
          </div>
          {/*<div style={{ flex: '0 0 10px' }} />*/}
        </div>
        <div style={{ flex: '0 0 10px' }} />
      </Scrollbox>
      <div style={{ flex: '0 0 30px' }} />
    </div>
  );
  //
} // SelectedItemsTab

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

type AccessState = 'accessible' | 'inaccessible' | 'checking';

function AccessLockIcon(props: { accessState: AccessState }) {
  const { t } = useTranslation();

  if (props.accessState === 'accessible') {
    return (
      <LockKeyholeOpen
        className="text-colour"
        data-text-green="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  if (props.accessState === 'inaccessible') {
    return (
      <LockKeyhole
        className="text-colour"
        data-text-red="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  return (
    <i
      className="pi pi-spin pi-spinner srctx-info"
      style={{
        fontSize: '1.8rem',
      }}
      title={t('Checking access to namespace')}
    />
  );
}
